import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /login/oauth — OAuth 2.1 login bridge for MCP Phase B (2026-05-30).
 *
 * Flow:
 *   1. /api/oauth/authorize redirects here with OAuth params in the query
 *   2. User enters email/password → POST /api/auth/login → JWT
 *   3. POST /api/oauth/authorize/complete with the JWT → server mints
 *      an auth code bound to this user
 *   4. window.location.href = redirect_to → Cowork's loopback listener
 *      catches the code → exchanges at /oauth/token → done.
 *
 * Visual: deliberately MUCH simpler than the main login. No marketing
 * copy. Just "Sign in to authorise <client_name>" + form.
 */
export default function LoginOAuthPage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Preserve every OAuth param exactly as we received it.
  const oauthParams = {
    client_id: params.get('client_id') || '',
    redirect_uri: params.get('redirect_uri') || '',
    scope: params.get('scope') || 'mcp:tools:read mcp:resources:read',
    state: params.get('state') || '',
    code_challenge: params.get('code_challenge') || '',
    code_challenge_method: params.get('code_challenge_method') || 'S256',
    resource: params.get('resource') || 'https://eipicenter.com/api/mcp/',
  };

  const missing = Object.entries(oauthParams).filter(([k, v]) => !v && k !== 'state');

  useEffect(() => {
    document.title = 'Sign in · EiPiCenter MCP';
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r1 = await axios.post(`${API}/auth/login`, { email, password });
      const token = r1.data?.token;
      if (!token) throw new Error('Login returned no token');

      const r2 = await axios.post(
        `${API}/oauth/authorize/complete`,
        oauthParams,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const redirectTo = r2.data?.redirect_to;
      if (!redirectTo) throw new Error('Authorize complete returned no redirect');
      window.location.href = redirectTo;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setBusy(false);
    }
  };

  const C = {
    bg: '#0a0b0d', panel: '#131519', line: '#23262d',
    ink: '#e7e9ec', inkDim: '#8b9099', inkFaint: '#565b63',
    accent: '#5fd0c5', accentDim: '#2f6b66', warn: '#e0a458',
    neutral: '#1a1c20',
    mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
    sans: "'IBM Plex Sans', system-ui, sans-serif",
  };

  if (missing.length) {
    return (
      <div data-testid="oauth-bad-request" style={{
        minHeight: '100vh', background: C.bg, color: C.ink,
        fontFamily: C.sans, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 24,
      }}>
        <div style={{
          maxWidth: 520, border: `1px solid ${C.warn}`,
          borderRadius: 8, padding: 28, background: C.panel,
        }}>
          <h2 style={{ fontFamily: C.mono, color: C.warn, margin: '0 0 12px' }}>
            Missing OAuth parameters
          </h2>
          <p style={{ lineHeight: 1.6, color: C.inkDim }}>
            This page is reached as part of the MCP OAuth flow. Visiting it
            directly won&apos;t work. Missing:{' '}
            <code style={{ fontFamily: C.mono, color: C.ink }}>
              {missing.map(([k]) => k).join(', ')}
            </code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="oauth-login-page" style={{
      minHeight: '100vh', background: C.bg, color: C.ink,
      fontFamily: C.sans, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24,
    }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600&display=swap" />
      <form
        onSubmit={onSubmit}
        style={{
          width: '100%', maxWidth: 420, background: C.panel,
          border: `1px solid ${C.line}`, borderRadius: 8,
          padding: '28px 28px 24px', display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <b style={{ fontFamily: C.mono, fontSize: 14, letterSpacing: '.5px' }}>EiPiCenter</b>
          <span style={{
            fontFamily: C.mono, fontSize: 10, color: C.accent,
            border: `1px solid ${C.accentDim}`, padding: '2px 6px',
            borderRadius: 3, letterSpacing: 1,
          }}>MCP · OAUTH</span>
        </div>
        <div style={{
          fontFamily: C.mono, fontSize: 11, color: C.inkFaint, lineHeight: 1.5,
        }}>
          Sign in to authorise{' '}
          <span style={{ color: C.ink }}>
            {oauthParams.client_id.replace(/^mcp-/, '').slice(0, 16) || 'this MCP client'}
          </span>
          {' '}to access your EiPiCenter account.
          <br/>
          Requested scopes: <span style={{ color: C.inkDim }}>{oauthParams.scope}</span>
        </div>

        {error && (
          <div data-testid="oauth-login-error" style={{
            fontFamily: C.mono, fontSize: 12, color: C.warn,
            border: `1px solid ${C.warn}40`, padding: '8px 10px', borderRadius: 4,
            background: 'rgba(224,164,88,0.06)', lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontFamily: C.mono, fontSize: 10, color: C.inkFaint,
            letterSpacing: 1, textTransform: 'uppercase' }}>Email</span>
          <input
            data-testid="oauth-email"
            type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@eipimedia.com"
            style={{
              background: C.neutral, color: C.ink,
              border: `1px solid ${C.line}`, borderRadius: 4,
              padding: '8px 10px', fontFamily: C.sans, fontSize: 13,
              outline: 'none',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontFamily: C.mono, fontSize: 10, color: C.inkFaint,
            letterSpacing: 1, textTransform: 'uppercase' }}>Password</span>
          <input
            data-testid="oauth-password"
            type="password" required autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            style={{
              background: C.neutral, color: C.ink,
              border: `1px solid ${C.line}`, borderRadius: 4,
              padding: '8px 10px', fontFamily: C.sans, fontSize: 13,
              outline: 'none',
            }}
          />
        </label>

        <button
          data-testid="oauth-submit" type="submit" disabled={busy}
          style={{
            fontFamily: C.mono, fontSize: 12, letterSpacing: '.4px',
            cursor: busy ? 'not-allowed' : 'pointer',
            background: C.accentDim, color: '#eafffb',
            border: `1px solid ${C.accent}`, padding: '10px 14px',
            borderRadius: 5, opacity: busy ? 0.6 : 1, marginTop: 4,
          }}
        >
          {busy ? 'Signing in…' : 'Sign in & authorise'}
        </button>

        <div style={{
          fontFamily: C.mono, fontSize: 10, color: C.inkFaint, lineHeight: 1.5,
          paddingTop: 4,
        }}>
          Only <span style={{ color: C.inkDim }}>@eipimedia.com</span> accounts are accepted.
        </div>
      </form>
    </div>
  );
}
